import React, { useMemo, useState, useEffect } from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Radar
} from 'recharts';
import {
  X, CheckCircle, AlertTriangle, XCircle,
  Clock, DollarSign, Eye, FileText, Layers, Download, MessageSquare, Lock, CreditCard,
  Leaf, Factory, Plane, Zap
} from 'lucide-react';

// Fallback ESG palette — only used when an execution has no milestones at all
// (legacy data). New assessments (DORA / custom) drive the breakdown
// dynamically from execution.milestones below.
const FALLBACK_CATEGORIES = { general: 'General & DMA', environmental: 'Environmental', social: 'Social', governance: 'Governance' };
const FALLBACK_CAT_COLORS = { general: '#10b981', environmental: '#22c55e', social: '#3b82f6', governance: '#a855f7' };
const MILESTONE_PALETTE = ['#0f766e', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#06b6d4', '#ec4899', '#84cc16', '#f97316'];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-lg text-sm">
      <p className="font-medium text-gray-900">{payload[0].name || payload[0].payload?.name}</p>
      <p className="text-gray-600">{payload[0].value}%</p>
    </div>
  );
};

export default function ExecutionDetailModal({ detail, onClose, actions, onDownloadPdf, isPaid = true, onPayment }) {
  const execution = detail?.execution || detail || {};
  const questions = detail?.questions || [];
  const milestones = detail?.milestones || [];
  const reworkRequests = detail?.rework_requests || [];

  const totalScore = execution.total_score || 0;
  const catScores = execution.category_scores || {};
  const answers = execution.answers || [];

  const qMap = useMemo(() => {
    const m = {};
    for (const q of questions) m[q.id] = q;
    return m;
  }, [questions]);

  const msMap = useMemo(() => {
    const m = {};
    for (const ms of milestones) m[ms.id] = ms;
    return m;
  }, [milestones]);

  // Helper to find option by ID or status
  const findOption = (q, ans) => {
    const options = q?.answer_options || [];
    // Try ID match first
    const idMatch = options.find(o => o.id === ans.answer_option_id);
    if (idMatch) return idMatch;
    // Try status match
    const status = (ans.status || '').toLowerCase();
    return options.find(o => (o.name || '').toLowerCase() === status || (o.name || '').toLowerCase().includes(status));
  };

  // Compute milestone scores
  const milestoneScores = useMemo(() => {
    const scores = {};
    for (const ms of milestones) {
      scores[ms.id] = { name: ms.name, subdomain: ms.subdomain, earned: 0, max: 0, count: 0, naCount: 0 };
    }
    for (const ans of answers) {
      const q = qMap[ans.question_id];
      if (!q) continue;
      const ms = scores[q.milestone_id];
      if (!ms) continue;
      const opt = findOption(q, ans);
      if (opt?.is_na) { ms.naCount++; continue; }
      ms.count++;
      ms.earned += opt?.points || 0;
      ms.max += q.points || 0;
    }
    return Object.entries(scores).map(([id, s]) => ({
      id, ...s, pct: s.max > 0 ? (s.earned / s.max) * 100 : 0
    }));
  }, [answers, milestones, qMap]);

  // Answer distribution
  const answerStats = useMemo(() => {
    let yes = 0, partial = 0, no = 0, na = 0;
    for (const ans of answers) {
      const q = qMap[ans.question_id];
      if (!q) {
        // Fallback to status if no question found
        const status = (ans.status || '').toLowerCase();
        if (status === 'yes') yes++;
        else if (status.includes('partial')) partial++;
        else if (status === 'no') no++;
        else if (status === 'n/a' || status === 'na') na++;
        continue;
      }
      const opt = findOption(q, ans);
      if (!opt) {
        // Fallback to status
        const status = (ans.status || '').toLowerCase();
        if (status === 'yes') yes++;
        else if (status.includes('partial')) partial++;
        else if (status === 'no') no++;
        continue;
      }
      const name = (opt.name || '').toLowerCase();
      if (opt.is_na) na++;
      else if (name === 'yes') yes++;
      else if (name.includes('partial') || name.includes('limited')) partial++;
      else no++;
    }
    return { yes, partial, no, na };
  }, [answers, qMap]);

  // Answers grouped by milestone
  const answersByMilestone = useMemo(() => {
    const groups = {};
    for (const ans of answers) {
      const q = qMap[ans.question_id];
      if (!q) continue;
      const msId = q.milestone_id;
      if (!groups[msId]) groups[msId] = [];
      const opt = findOption(q, ans);
      const optName = opt?.name || ans.status || '-';
      groups[msId].push({ question: q, answer: ans, optionName: optName, isNa: opt?.is_na, points: opt?.points ?? 0 });
    }
    return groups;
  }, [answers, qMap]);
  // Supporting evidence: answers that have comments or uploaded files
  const supportingEvidence = useMemo(() => {
    const out = [];
    for (const ans of answers) {
      const q = qMap[ans.question_id];
      if (!q) continue;
      const fileIds = ans.uploaded_file_ids || ans.document_ids || [];
      const comment = ans.comment_text;
      if (fileIds.length === 0 && !comment) continue;
      const opt = findOption(q, ans);
      out.push({
        question: q,
        optionName: opt?.name || ans.status || '-',
        comment,
        fileIds,
      });
    }
    return out;
  }, [answers, qMap]);

  // Fetch admin-uploaded recommendation files (only when paid and execution id exists)
  const [recommendationFiles, setRecommendationFiles] = useState([]);
  useEffect(() => {
    if (!isPaid || !execution?.id) { setRecommendationFiles([]); return; }
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/executions/${execution.id}/recommendation-files`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setRecommendationFiles(Array.isArray(data) ? data : []))
      .catch(() => setRecommendationFiles([]));
  }, [isPaid, execution?.id]);


  const pieData = [
    { name: 'Yes', value: answerStats.yes, color: '#22c55e' },
    { name: 'Partial', value: answerStats.partial, color: '#f59e0b' },
    { name: 'No', value: answerStats.no, color: '#ef4444' },
    { name: 'N/A', value: answerStats.na, color: '#94a3b8' },
  ].filter(d => d.value > 0);

  // Dynamically derive the breakdown buckets from milestones if available,
  // otherwise fall back to the legacy ESG categories so old executions still render.
  const breakdown = useMemo(() => {
    if (milestones && milestones.length > 0) {
      return milestones.map((ms, i) => {
        const score = milestoneScores.find(s => s.id === ms.id);
        return {
          key: ms.id,
          label: ms.name,
          shortLabel: ms.name.length > 14 ? ms.name.slice(0, 12) + '…' : ms.name,
          score: parseFloat((score?.pct || 0).toFixed(1)),
          color: MILESTONE_PALETTE[i % MILESTONE_PALETTE.length],
        };
      });
    }
    return Object.entries(FALLBACK_CATEGORIES).map(([key, label]) => ({
      key,
      label,
      shortLabel: label.split(' ')[0],
      score: parseFloat((catScores[key] || 0).toFixed(1)),
      color: FALLBACK_CAT_COLORS[key],
    }));
  }, [milestones, milestoneScores, catScores]);

  const barData = breakdown.map(b => ({ name: b.shortLabel, score: b.score, fill: b.color }));
  const radarData = breakdown.map(b => ({ category: b.shortLabel, score: b.score, fullMark: 100 }));

  const scoreBg = (s) => s >= 70 ? 'from-green-500 to-emerald-600' : s >= 40 ? 'from-amber-500 to-orange-600' : 'from-red-500 to-rose-600';
  const scoreLevel = (s) => s >= 80 ? 'Excellent' : s >= 60 ? 'Good' : s >= 40 ? 'Fair' : 'Needs Improvement';
  const scoreColor = (s) => s >= 70 ? 'text-green-600' : s >= 40 ? 'text-amber-600' : 'text-red-600';
  const barColor = (s) => s >= 70 ? '#22c55e' : s >= 40 ? '#f59e0b' : '#ef4444';

  const STATUS_MAP = {
    submitted: { label: 'Submitted', cls: 'bg-blue-50 text-blue-700', icon: Clock },
    under_review: { label: 'Under Review', cls: 'bg-amber-50 text-amber-700', icon: Eye },
    rework_required: { label: 'Rework Required', cls: 'bg-orange-50 text-orange-700', icon: AlertTriangle },
    approved: { label: 'Approved', cls: 'bg-green-50 text-green-700', icon: CheckCircle },
    paid: { label: 'Paid', cls: 'bg-emerald-50 text-emerald-700', icon: DollarSign },
  };

  const statusInfo = STATUS_MAP[execution.status] || STATUS_MAP.submitted;
  const StatusIcon = statusInfo.icon;

  const optBadge = (name, isNa) => {
    if (isNa) return 'bg-gray-100 text-gray-500';
    const n = (name || '').toLowerCase();
    if (n === 'yes') return 'bg-green-50 text-green-700';
    if (n.includes('partial') || n.includes('limited')) return 'bg-amber-50 text-amber-700';
    if (n === 'no') return 'bg-red-50 text-red-700';
    return 'bg-emerald-50 text-emerald-700';
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-start justify-center z-50 overflow-auto py-8" onClick={onClose}>
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="execution-detail-modal">

        {/* Header */}
        <div className={`bg-gradient-to-br ${scoreBg(totalScore)} text-white px-8 py-6`}>
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold mb-1">{execution.company_name || 'Assessment'}</h2>
              <p className="text-white/80 text-sm">{execution.contact_email}</p>
              <div className="flex items-center gap-3 mt-3">
                <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                  <StatusIcon className="w-3 h-3" /> {statusInfo.label}
                </span>
                <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-medium">
                  {execution.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                </span>
                <span className="text-white/70 text-xs">
                  {execution.submitted_at ? new Date(execution.submitted_at).toLocaleDateString() : ''}
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-5xl font-bold">{totalScore.toFixed(1)}%</p>
              <p className="text-white/80 text-sm mt-1">{scoreLevel(totalScore)}</p>
            </div>
          </div>
          <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-8 space-y-6 max-h-[70vh] overflow-auto">

          {/* Creator/verifier badge — shown when admin/agent initiated this assessment */}
          {(execution.created_by_role === 'admin' || execution.created_by_role === 'agent') && (
            <div data-testid={`creator-badge-${execution.created_by_role}`}
              className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold ${
                execution.created_by_role === 'admin'
                  ? 'bg-violet-50 border-violet-200 text-violet-700'
                  : 'bg-blue-50 border-blue-200 text-blue-700'
              }`}>
              <CheckCircle className="w-4 h-4" />
              <span>Created and verified by {execution.created_by_role}: {execution.created_by_name || execution.created_by_role}</span>
            </div>
          )}
          {/* Breakdown Score Cards (dynamic per assessment milestones) */}
          <div className={`grid gap-3 ${breakdown.length <= 4 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'}`}>
            {breakdown.map(b => (
              <div key={b.key} className="bg-white border border-gray-200 rounded-xl p-4 text-center" data-testid={`breakdown-score-${b.key}`}>
                <p className="text-xs text-gray-500 mb-1 truncate" title={b.label}>{b.label}</p>
                <p className={`text-2xl font-bold ${scoreColor(b.score)}`}>{b.score.toFixed(1)}%</p>
                <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${b.score}%`, backgroundColor: b.color }} />
                </div>
              </div>
            ))}
          </div>

          {/* Charts Row - VISIBLE for unpaid to tease data */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Pie - Answer Dist */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Answer Distribution</h3>
              {pieData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={3} dataKey="value">
                        {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex justify-center gap-4 mt-2">
                    {pieData.map(d => (
                      <div key={d.name} className="flex items-center gap-1.5 text-xs text-gray-600">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                        {d.name} ({d.value})
                      </div>
                    ))}
                  </div>
                </>
              ) : <p className="text-gray-400 text-sm text-center py-8">No answer data</p>}
            </div>

            {/* Radar */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Performance Radar</h3>
              <ResponsiveContainer width="100%" height={210}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#e5e7eb" />
                  <PolarAngleAxis dataKey="category" tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: '#9ca3af' }} />
                  <Radar name="Score" dataKey="score" stroke="#10b981" fill="#10b981" fillOpacity={0.2} strokeWidth={2} />
                  <Tooltip content={<CustomTooltip />} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Breakdown Bar Chart */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{milestones.length > 0 ? 'Score by Milestone' : 'Category Comparison'}</h3>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={barData} barSize={40}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                  {barData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3">
              <CheckCircle className="w-7 h-7 text-green-600 flex-shrink-0" />
              <div>
                <p className="text-xl font-bold text-green-700">{answerStats.yes}</p>
                <p className="text-xs text-green-600">Compliant</p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3">
              <AlertTriangle className="w-7 h-7 text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-xl font-bold text-amber-700">{answerStats.partial}</p>
                <p className="text-xs text-amber-600">Partial</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-3">
              <XCircle className="w-7 h-7 text-red-600 flex-shrink-0" />
              <div>
                <p className="text-xl font-bold text-red-700">{answerStats.no}</p>
                <p className="text-xs text-red-600">Non-Compliant</p>
              </div>
            </div>
          </div>

          {/* Milestone Scores */}
          {milestoneScores.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Layers className="w-4 h-4 text-emerald-600" /> Score by Milestone
              </h3>
              <div className="space-y-3">
                {milestoneScores.map(ms => (
                  <div key={ms.id} data-testid={`milestone-score-${ms.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-800">{ms.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">{ms.earned}/{ms.max} pts</span>
                        {ms.naCount > 0 && <span className="text-xs text-gray-400">({ms.naCount} N/A)</span>}
                        <span className={`text-sm font-bold ${scoreColor(ms.pct)}`}>{ms.pct.toFixed(1)}%</span>
                      </div>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${ms.pct}%`, backgroundColor: barColor(ms.pct) }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Carbon Footprint Summary */}
          {execution.carbon_data?.result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5" data-testid="carbon-data-section">
              <h3 className="text-sm font-semibold text-emerald-800 mb-4 flex items-center gap-2">
                <Leaf className="w-4 h-4" /> Carbon Footprint Summary
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-white border border-emerald-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">Total Emissions</p>
                  <p className="text-lg font-bold text-emerald-700">{execution.carbon_data.result.total || 0}</p>
                  <p className="text-xs text-gray-400">tCO2e/year</p>
                </div>
                <div className="bg-white border border-amber-200 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Factory className="w-3 h-3 text-amber-600" />
                    <span className="text-xs text-gray-500">Scope 1</span>
                  </div>
                  <p className="text-lg font-bold text-amber-700">{execution.carbon_data.result.scope1 || 0}</p>
                  <p className="text-xs text-gray-400">{execution.carbon_data.result.scope1_percentage || 0}%</p>
                </div>
                <div className="bg-white border border-blue-200 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Zap className="w-3 h-3 text-blue-600" />
                    <span className="text-xs text-gray-500">Scope 2</span>
                  </div>
                  <p className="text-lg font-bold text-blue-700">{execution.carbon_data.result.scope2 || 0}</p>
                  <p className="text-xs text-gray-400">{execution.carbon_data.result.scope2_percentage || 0}%</p>
                </div>
                <div className="bg-white border border-purple-200 rounded-lg p-3 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Plane className="w-3 h-3 text-purple-600" />
                    <span className="text-xs text-gray-500">Scope 3</span>
                  </div>
                  <p className="text-lg font-bold text-purple-700">{execution.carbon_data.result.scope3 || 0}</p>
                  <p className="text-xs text-gray-400">{execution.carbon_data.result.scope3_percentage || 0}%</p>
                </div>
              </div>
              
              {/* Top Emission Sources */}
              {execution.carbon_data.result.top_sources?.length > 0 && (
                <div className="bg-white border border-emerald-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-gray-700 mb-2">Top Emission Sources</p>
                  <div className="space-y-2">
                    {execution.carbon_data.result.top_sources.slice(0, 3).map((src, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{src.source}</span>
                        <span className="font-medium text-emerald-700">{src.emissions} tCO2e</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Carbon Intensity */}
              {execution.carbon_data.result.intensity_per_revenue && (
                <div className="mt-3 pt-3 border-t border-emerald-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Carbon Intensity</span>
                    <span className="font-medium text-emerald-700">
                      {execution.carbon_data.result.intensity_per_revenue} tCO2e/M€
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Rework Requests */}
          {reworkRequests.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-orange-700 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Rework Requests ({reworkRequests.length})
              </h3>
              <div className="space-y-2">
                {reworkRequests.map((rw, i) => {
                  const q = qMap[rw.question_id];
                  return (
                    <div key={i} className="bg-white border border-orange-200 rounded-lg p-3">
                      <p className="text-sm text-gray-900">{q?.question_text || rw.question_id}</p>
                      <p className="text-xs text-orange-600 mt-1">{rw.reason}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Supporting Evidence — comments + uploaded docs from user */}
          {supportingEvidence.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="supporting-evidence-section">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-emerald-600" /> Supporting Evidence ({supportingEvidence.length})
              </h3>
              <div className="space-y-3">
                {supportingEvidence.map((ev, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start justify-between gap-3 mb-1.5">
                      <p className="text-sm text-gray-800 flex-1">{ev.question.question_text}</p>
                      <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded whitespace-nowrap">{ev.optionName}</span>
                    </div>
                    {ev.comment && (
                      <p className="text-xs text-gray-700 bg-gray-50 rounded px-2.5 py-1.5 whitespace-pre-wrap mt-1">{ev.comment}</p>
                    )}
                    {ev.fileIds.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {ev.fileIds.map(fid => (
                          <a key={fid} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}`} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded hover:bg-blue-100">
                            <Download className="w-3 h-3" /> Document
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Answers by Milestone - Blurred for unpaid */}
          {isPaid ? (
            milestones.map(ms => {
              const items = answersByMilestone[ms.id] || [];
              if (items.length === 0) return null;
              const msScore = milestoneScores.find(s => s.id === ms.id);
              return (
                <div key={ms.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{ms.name}</span>
                      <span className="text-xs text-gray-400 capitalize">{ms.subdomain}</span>
                    </div>
                    {msScore && <span className={`text-sm font-bold ${scoreColor(msScore.pct)}`}>{msScore.pct.toFixed(1)}%</span>}
                  </div>
                  <div className="divide-y divide-gray-100">
                    {items.map((item, i) => {
                      const fileIds = item.answer.uploaded_file_ids || item.answer.document_ids || [];
                      const ghg = item.answer.ghg_data;
                      const rec = item.question.answer_options?.find(o => o.id === item.answer.answer_option_id)?.recommendation_details;
                      const comment = item.answer.comment_text;
                      return (
                        <div key={i} className={`px-5 py-3 ${item.answer.requires_rework ? 'bg-orange-50' : ''}`}>
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-800 flex-1 pr-4">{item.question.question_text}</p>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-400">{item.points}/{item.question.points || 0}</span>
                              <span className={`text-xs font-medium px-2.5 py-1 rounded-lg whitespace-nowrap ${optBadge(item.optionName, item.isNa)}`}>
                                {item.optionName}
                              </span>
                            </div>
                          </div>
                          {rec && <p className="text-xs text-amber-600 mt-1 ml-0">Rec: {rec}</p>}
                          {comment && (
                            <div className="mt-2 bg-emerald-50/60 border border-emerald-100 rounded px-2.5 py-1.5" data-testid={`answer-comment-${item.question.id}`}>
                              <div className="flex items-start gap-1.5">
                                <MessageSquare className="w-3 h-3 text-emerald-600 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-gray-700 whitespace-pre-wrap">{comment}</p>
                              </div>
                            </div>
                          )}
                          {fileIds.length > 0 && (
                            <div className="flex gap-2 mt-1.5">
                              {fileIds.map(fid => (
                                <a key={fid} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}`} target="_blank" rel="noopener noreferrer"
                                  data-testid={`answer-file-link-${fid}`}
                                  className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded hover:bg-blue-100">
                                  <Download className="w-3 h-3" /> File
                                </a>
                              ))}
                            </div>
                          )}
                          {ghg && (
                            <div className="mt-1.5 text-xs text-gray-500">
                              GHG: {Object.entries(ghg).filter(([,v]) => v?.emissions).map(([scope, v]) => `${scope}: ${v.emissions} tCO2e`).join(', ') || 'No data'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-8 text-center">
              <Lock className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Detailed Answers Locked</h3>
              <p className="text-sm text-gray-500 mb-4">
                Unlock your full assessment report to see detailed answers, recommendations, and improvement areas.
              </p>
              {onPayment && (
                <button onClick={onPayment} data-testid="unlock-report-modal-btn"
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 mx-auto">
                  <CreditCard className="w-5 h-5" /> Unlock Full Report
                </button>
              )}
            </div>
          )}

          {/* Admin Recommendation Documents (paid only, for sub-optimal answers) */}
          {isPaid && recommendationFiles.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5" data-testid="recommendation-docs-section">
              <h3 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Additional Recommendation Documents ({recommendationFiles.length})
              </h3>
              <p className="text-xs text-amber-700 mb-3">
                Curated guidance from our team to help you improve the areas where you scored below the maximum.
              </p>
              <div className="space-y-2">
                {recommendationFiles.map((r, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 bg-white border border-amber-200 rounded-lg px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate">{r.question_text}</p>
                      <p className="text-xs text-amber-600 mt-0.5">Your answer: {r.selected_answer}</p>
                    </div>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.file_id}`} target="_blank" rel="noopener noreferrer"
                      data-testid={`download-recommendation-${r.question_id}`}
                      className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium px-3 py-1.5 rounded transition-colors whitespace-nowrap">
                      <Download className="w-3.5 h-3.5" /> Download
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Approval Comment */}
          {execution.approval_comment && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> Agent Review Comment
              </h3>
              <p className="text-sm text-gray-800 italic">"{execution.approval_comment}"</p>
            </div>
          )}

          {/* Actions + PDF Download */}
          <div className="flex flex-wrap gap-3 pt-2">
            {onDownloadPdf && (
              <button onClick={onDownloadPdf} data-testid="download-pdf-btn"
                className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium text-sm transition-colors bg-gray-900 hover:bg-gray-800 text-white">
                <Download className="w-4 h-4" /> Download PDF
              </button>
            )}
            {actions && actions.map((action, i) => (
              <button key={i} onClick={action.onClick} data-testid={action.testId}
                className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium text-sm transition-colors ${action.className}`}>
                {action.icon && <action.icon className="w-4 h-4" />}
                {action.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
